import {
  type FunctionComponent,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import { cn } from 'tailwind-v4/lib/utils'
import { UserAvatar } from 'tailwind-v4/components/user-avatar'
import { gravatarImage } from '@/utils/common'

export type MessageDirection = 'incoming' | 'outgoing' | 'note' | 'submission'

/**
 * Shared styles for HTML message bodies rendered with
 * `dangerouslySetInnerHTML`. Connect has no global `prose`, so the email /
 * newsletter formatting (headings, lists, links, images, quotes, tables) is
 * applied here so incoming + outgoing mail renders Gmail-style rather than as
 * a flat blob of text.
 */
export const emailBodyClasses = cn(
  'text-sm leading-relaxed break-words text-[var(--foreground)]',
  '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
  '[&_a]:text-blue-600 [&_a]:underline dark:[&_a]:text-blue-400',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5',
  '[&_h1]:my-3 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:my-2.5 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:my-2 [&_h3]:text-base [&_h3]:font-semibold',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-black/15 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-black/70 dark:[&_blockquote]:border-white/15 dark:[&_blockquote]:text-white/70',
  '[&_img]:my-2 [&_img]:max-w-full [&_img]:rounded-md',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-black/10 [&_td]:p-1.5 [&_th]:border [&_th]:border-black/10 [&_th]:p-1.5 dark:[&_td]:border-white/10 dark:[&_th]:border-white/10',
  '[&_hr]:my-3 [&_hr]:border-black/10 dark:[&_hr]:border-white/10'
)

const splitName = (name: string) => {
  const parts = (name || '').trim().split(/\s+/)
  return {
    first_name: parts[0] ?? '',
    last_name: parts.length > 1 ? parts[parts.length - 1] : '',
  }
}

const LABELS: Record<MessageDirection, string | null> = {
  incoming: null,
  outgoing: 'Sent',
  note: 'Internal note',
  submission: 'Form submission',
}

interface MessageRowProps {
  direction: MessageDirection
  name: string
  email?: string
  /** Secondary line under the name — recipient, subject, channel, etc. */
  meta?: ReactNode
  time?: ReactNode
  /** Read-receipt ticks / sending state, shown next to the time. */
  status?: ReactNode
  /** Detail popover trigger, revealed on row hover. */
  info?: ReactNode
}

/**
 * Gmail / bulletin-style message row. Replaces the old SMS chat bubbles —
 * every message is a full-width stacked card with an avatar + sender header
 * and the body rendered beneath it, so long-form email renders like email.
 */
export const MessageRow: FunctionComponent<
  PropsWithChildren<MessageRowProps>
> = ({ direction, name, email = '', meta, time, status, info, children }) => {
  const label = LABELS[direction]
  const { first_name, last_name } = splitName(name)

  return (
    <article
      className={cn(
        // Full-width rows separated by a faint divider — no boxes. The note
        // type gets a subtle amber wash so internal notes still stand out.
        'new-ui-theme group border-t border-black/[0.06] px-1 py-4 text-[var(--foreground)] dark:border-white/[0.07]',
        direction === 'note' && 'bg-amber-50/50 dark:bg-amber-400/[0.04]'
      )}
    >
      <header className="flex items-start gap-3">
        <UserAvatar
          user={{ first_name, last_name, email }}
          resolveAvatarUrl={email ? gravatarImage : undefined}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-medium">{name}</span>
              {label ? (
                <span className="shrink-0 rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)] dark:bg-white/10">
                  {label}
                </span>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1.5 text-[11px] text-[var(--muted-foreground)]">
              {time}
              {status}
              {info ? (
                <span className="opacity-0 transition-opacity group-hover:opacity-100">
                  {info}
                </span>
              ) : null}
            </div>
          </div>
          {meta ? (
            <p className="truncate text-xs text-[var(--muted-foreground)]">
              {meta}
            </p>
          ) : null}
        </div>
      </header>

      <div className="pl-[2.75rem] pt-2">{children}</div>
    </article>
  )
}
